/** Split text content into paragraphs by double newlines or single newlines.
 *  Filters out empty paragraphs — suitable for display/rendering. */
export const parseTextIntoParagraphs = (content: string | null): string[] => {
    if (!content?.trim()) return [];
    // Split by double newline first; if that yields only one block, split by single newline
    const byDouble = content.split(/\n\n+/).filter(p => p.trim());
    if (byDouble.length > 1) return byDouble;
    return content.split(/\n/).filter(p => p.trim());
};

/** Split text into paragraphs preserving empty ones.
 *  Use this for positional logic (e.g. mapping a cursor offset to a paragraph index)
 *  where empty paragraphs must be accounted for. */
export const splitParagraphsRaw = (content: string): string[] => {
    if (!content) return [''];
    const byDouble = content.split(/\n\n+/);
    if (byDouble.length > 1) return byDouble;
    return content.split(/\n/);
};

/** Build annotation key for an entity */
export const buildAnnotationKey = (entityType: string, entityId: number, variant: 'logline' | 'plot') =>
    `${entityType}-${entityId}-${variant}`;

export interface ParagraphLineRange {
    start: number; // inclusive char offset
    end: number; // exclusive char offset
    line_ids: number[];
}

export interface ParagraphLineEntry {
    /** Primary identity (since slice 4): in-memory this is the line's stable
     *  `lineId`; on the wire it's the positional `lineIndex`. The boundary
     *  translation in `translateParagraphLineIndices` swaps the two. */
    index: number;
    /** Composite block context (slice 7). Populated in-memory after
     *  hydrate via `enrichEntriesWithBlockContext` so consumers can group
     *  annotations by block (e.g. all entries on a Dialog speaker turn).
     *  Optional on the wire — entries persisted before the composite rollout
     *  don't carry it and the hydrate path re-derives. */
    blockId?: number;
    /** Composite sub-field context (`character` / `phrase` / `text` / …). Same
     *  semantics as `blockId`: derived on hydrate, persisted as a hint. */
    subField?: SubField;
    line_ids?: number[]; // whole-paragraph lines (painting mode + backward compat)
    ranges?: ParagraphLineRange[]; // sub-paragraph ranges (selection mode)
}

/** How many metadata rows follow the scene heading in the CURRENT skeleton
 *  (heading = 0, then LOGLINE and PLOT, body starts at `1 + SCENE_META_ROWS`).
 *  Bump when a new permanent slot is added — see `ContentJsonData.meta_rows`. */
export const SCENE_META_ROWS = 2;

export interface ContentJsonData {
    /** Skeleton version of the positional space (#1835): the number of
     *  metadata rows that followed the scene heading when the indices below
     *  were recorded. Absent = 1 (logline only, the pre-#1835 skeleton); the
     *  PLOT slot made it 2. Readers shift persisted body indices
     *  (`index > meta_rows`) by `SCENE_META_ROWS - meta_rows` before
     *  resolving lineIds, so data recorded against the old skeleton doesn't
     *  land one row above its line. */
    meta_rows?: number;
    paragraph_lines?: ParagraphLineEntry[];
    /** Draft-block flags (draft blocks — «черновики»). On the wire: the
     *  positional `lineIndex` of every line belonging to a draft block, like
     *  `paragraph_lines[].index`. In memory this data does NOT live here — it
     *  is engine state (`EngineState.draftLineIds`, lineId-keyed); the save
     *  composer projects it in and the hydrate path dispatches it out
     *  (`hydrateDraftLines`), so a stale copy can never leak back to the
     *  wire. */
    draft_line_ids?: number[];
    /** Replica pins («Реплика» rows the parser would read as «Действие»). On the
     *  wire: the positional `lineIndex` of every ORPHANED replica line — dialogue
     *  with no «Персонаж» above it, the one shape Fountain cannot express (there
     *  is no forcing prefix for dialogue). An empty replica and the two halves of
     *  a cut are NOT here: those are written into the text itself as Fountain's
     *  connected blank. Same positional space as `draft_line_ids`, so whatever
     *  shifts those on read shifts these. In memory the data lives in the engine
     *  (`EngineState.phraseAnchorLineIds`, lineId-keyed); the save composer
     *  projects it in and `hydratePhraseAnchors` dispatches it out. */
    replica_lines?: number[];
    /** Who set a draft line aside, and when (#1840): one entry per drafted
     *  line that carries a stamp, in the same positional space as
     *  `draft_line_ids` (so whatever shifts those indices on read shifts
     *  these). A drafted line with no entry was flagged before stamps existed.
     *  Engine state in memory (`EngineState.draftStampByLineId`), like the
     *  flags themselves. */
    draft_marks?: DraftMarkEntry[];
}

export interface DraftMarkEntry {
    /** Positional `lineIndex` of the stamped line, like `draft_line_ids[]`. */
    index: number;
    /** Display label of who set it aside; `''` means unknown. */
    user: string;
    /** ISO timestamp of when. */
    at: string;
}

/**
 * Translate persisted `draft_marks` (positional) to their live lineIds, same
 * contract as `translatePositionalLineIndices`: entries that fail to resolve are
 * dropped, and a mark's stamp travels with it.
 */
export const translateDraftMarks = (
    marks: readonly DraftMarkEntry[],
    lookup: (currentIndex: number) => number | undefined,
): { lineId: number; stamp: { user: string; at: string } }[] => {
    const translated: { lineId: number; stamp: { user: string; at: string } }[] = [];
    for (const mark of marks) {
        const lineId = lookup(mark.index);
        if (lineId !== undefined) translated.push({ lineId, stamp: { user: mark.user, at: mark.at } });
    }
    return translated;
};

/**
 * Translate an array of positional line indices between its two identities
 * (lineIndex on the wire ↔ stable lineId in memory), same contract as
 * `translateParagraphLineIndices`: entries that fail to resolve are dropped.
 * Shared by every engine fact that rides `content_json` this way —
 * `draft_line_ids` and `replica_lines`.
 */
export const translatePositionalLineIndices = (ids: readonly number[], lookup: (currentIndex: number) => number | undefined): number[] => {
    const translated: number[] = [];
    for (const id of ids) {
        const next = lookup(id);
        if (next !== undefined) translated.push(next);
    }
    return translated;
};

/**
 * Subset of `SubField` from `@/types/fountain` re-exported here so this module
 * stays free of the broader type graph (the wire format hangs off this file).
 * Kept literal so a typo in a persisted entry shows up as a type error.
 */
export type SubField = 'text' | 'character' | 'phrase' | 'intExt' | 'location' | 'sublocation' | 'title' | 'timeOfDay';

export interface TextSegment {
    text: string;
    line_ids: number[];
}

const arraysEqual = (a: number[], b: number[]): boolean => a.length === b.length && a.every((v, i) => v === b[i]);

export const normalizeRanges = (ranges: ParagraphLineRange[]): ParagraphLineRange[] => {
    if (ranges.length === 0) return [];

    const boundaries = new Set<number>();
    for (const r of ranges) {
        boundaries.add(r.start);
        boundaries.add(r.end);
    }
    const sorted = [...boundaries].sort((a, b) => a - b);
    if (sorted.length < 2) return ranges;

    const segments: ParagraphLineRange[] = [];
    for (let i = 0; i < sorted.length - 1; i++) {
        const start = sorted[i] as number;
        const end = sorted[i + 1] as number;
        const lineIds = new Set<number>();
        for (const r of ranges) {
            if (r.start <= start && r.end >= end) {
                for (const id of r.line_ids) lineIds.add(id);
            }
        }
        if (lineIds.size > 0) {
            segments.push({ start, end, line_ids: [...lineIds].sort((a, b) => a - b) });
        }
    }

    const merged: ParagraphLineRange[] = [];
    for (const seg of segments) {
        const prev = merged[merged.length - 1];
        if (prev && prev.end === seg.start && arraysEqual(prev.line_ids, seg.line_ids)) {
            prev.end = seg.end;
        } else {
            merged.push({ ...seg });
        }
    }
    return merged;
};

export const setLinesForRange = (
    data: ContentJsonData,
    paragraphIndex: number,
    start: number,
    end: number,
    lineIds: number[],
): ParagraphLineEntry[] => {
    const existing = data.paragraph_lines ?? [];
    const entry = existing.find(p => p.index === paragraphIndex);
    const currentRanges = entry?.ranges ?? [];

    const updatedRanges: ParagraphLineRange[] = [];
    for (const r of currentRanges) {
        if (r.end <= start || r.start >= end) {
            updatedRanges.push(r);
        } else {
            if (r.start < start) {
                updatedRanges.push({ start: r.start, end: start, line_ids: [...r.line_ids] });
            }
            if (r.end > end) {
                updatedRanges.push({ start: end, end: r.end, line_ids: [...r.line_ids] });
            }
        }
    }

    if (lineIds.length > 0) {
        updatedRanges.push({ start, end, line_ids: lineIds });
    }

    const normalized = normalizeRanges(updatedRanges);

    if (entry) {
        const updated = existing.map(p =>
            p.index === paragraphIndex ? { ...p, ranges: normalized.length > 0 ? normalized : undefined } : p,
        );
        return updated.filter(p => (p.line_ids && p.line_ids.length > 0) || (p.ranges && p.ranges.length > 0));
    }

    if (normalized.length === 0) return existing;
    return [...existing, { index: paragraphIndex, ranges: normalized }];
};

export const getLineIdsForRange = (data: ContentJsonData, paragraphIndex: number, start: number, end: number): number[] => {
    const entry = data.paragraph_lines?.find(p => p.index === paragraphIndex);
    if (!entry?.ranges) return [];
    const ids = new Set<number>();
    for (const r of entry.ranges) {
        if (r.start < end && r.end > start) {
            for (const id of r.line_ids) ids.add(id);
        }
    }
    return [...ids];
};

export const splitTextByRanges = (text: string, ranges: ParagraphLineRange[]): TextSegment[] => {
    if (ranges.length === 0) return [{ text, line_ids: [] }];

    const sorted = [...ranges].sort((a, b) => a.start - b.start);
    const segments: TextSegment[] = [];
    let cursor = 0;

    for (const r of sorted) {
        const start = Math.max(r.start, 0);
        const end = Math.min(r.end, text.length);
        if (start >= end) continue;

        if (cursor < start) {
            segments.push({ text: text.slice(cursor, start), line_ids: [] });
        }
        segments.push({ text: text.slice(start, end), line_ids: r.line_ids });
        cursor = end;
    }

    if (cursor < text.length) {
        segments.push({ text: text.slice(cursor), line_ids: [] });
    }

    return segments;
};

export const getAllLineIdsForParagraph = (entry: ParagraphLineEntry): number[] => {
    const ids = new Set<number>();
    if (entry.line_ids) {
        for (const id of entry.line_ids) ids.add(id);
    }
    if (entry.ranges) {
        for (const r of entry.ranges) {
            for (const id of r.line_ids) ids.add(id);
        }
    }
    return [...ids];
};

/**
 * Union of every line id mentioned in a raw wire-format `content_json`
 * (whole-paragraph `line_ids` + range `line_ids`). Lets scene cards surface
 * lines attached in the editor (#1830). `draft_line_ids` are positional
 * indices, not line ids — never included.
 */
export const collectContentJsonLineIds = (raw: string | object | null | undefined): number[] => {
    if (!raw) return [];
    let parsed: unknown = raw;
    if (typeof raw === 'string') {
        try {
            parsed = JSON.parse(raw);
        } catch {
            return [];
        }
    }
    const data = (Array.isArray(parsed) ? parsed[0] : parsed) as ContentJsonData | undefined;
    if (!data?.paragraph_lines) return [];
    const ids = new Set<number>();
    for (const entry of data.paragraph_lines) {
        for (const id of getAllLineIdsForParagraph(entry)) ids.add(id);
    }
    return [...ids];
};

/** Apply the line popover's selection to one paragraph (card popover Apply,
 *  #1830 mockup). The popover preselects the union of whole-paragraph and
 *  range-attached lines, so Apply must respect where each id came from:
 *  - still-selected whole-paragraph lines stay whole-paragraph;
 *  - still-selected range-only lines stay range-only (never promoted);
 *  - newly selected lines attach whole-paragraph;
 *  - deselected lines are removed from `line_ids` AND stripped from `ranges`.
 *  The entry is dropped when nothing remains. */
export const applyLineSelectionToParagraph = (
    data: ContentJsonData,
    paragraphIndex: number,
    selectedIds: number[],
): ParagraphLineEntry[] => {
    const existing = data.paragraph_lines ?? [];
    const entry = existing.find(p => p.index === paragraphIndex);
    const selected = new Set(selectedIds);

    const currentWhole = entry?.line_ids ?? [];
    const rangeIds = new Set<number>();
    for (const r of entry?.ranges ?? []) {
        for (const id of r.line_ids) rangeIds.add(id);
    }

    const keptWhole = currentWhole.filter(id => selected.has(id));
    const added = selectedIds.filter(id => !currentWhole.includes(id) && !rangeIds.has(id));
    const newWhole = [...keptWhole, ...added];

    const newRanges = (entry?.ranges ?? [])
        .map(r => ({ ...r, line_ids: r.line_ids.filter(id => selected.has(id)) }))
        .filter(r => r.line_ids.length > 0);

    if (!entry) {
        if (newWhole.length === 0) return existing;
        return [...existing, { index: paragraphIndex, line_ids: newWhole }];
    }
    const updated = existing.map(p =>
        p.index === paragraphIndex
            ? { ...p, line_ids: newWhole.length > 0 ? newWhole : undefined, ranges: newRanges.length > 0 ? newRanges : undefined }
            : p,
    );
    return updated.filter(p => (p.line_ids?.length ?? 0) > 0 || (p.ranges?.length ?? 0) > 0);
};

/** Toggle a line on/off for a whole paragraph (painting mode) */
export const toggleLineInData = (data: ContentJsonData, paragraphIndex: number, lineId: number): ParagraphLineEntry[] => {
    const existing = data.paragraph_lines ?? [];
    const entry = existing.find(p => p.index === paragraphIndex);

    if (entry) {
        const currentIds: number[] = entry.line_ids ?? [];
        const hasInIds = currentIds.includes(lineId);
        const hasInRanges = entry.ranges?.some(r => r.line_ids.includes(lineId)) ?? false;

        if (hasInIds || hasInRanges) {
            const newLineIds = currentIds.filter(id => id !== lineId);
            const newRanges = entry.ranges
                ?.map(r => ({ ...r, line_ids: r.line_ids.filter(id => id !== lineId) }))
                .filter(r => r.line_ids.length > 0);
            const updated = existing.map(p =>
                p.index === paragraphIndex ? { ...p, line_ids: newLineIds, ranges: newRanges?.length ? newRanges : undefined } : p,
            );
            return updated.filter(p => (p.line_ids?.length ?? 0) > 0 || (p.ranges?.length ?? 0) > 0);
        }
        const newLineIds = [...currentIds, lineId];
        return existing.map(p => (p.index === paragraphIndex ? { ...p, line_ids: newLineIds } : p));
    }
    return [...existing, { index: paragraphIndex, line_ids: [lineId] }];
};

/**
 * Translate paragraph_lines whose `index` field carries one identity into the
 * other identity. `lookup(currentIndex) → newIndex | undefined` is called once
 * per entry; entries that fail to resolve (line was deleted, or no current
 * lineIndex maps to that lineId) are dropped.
 *
 * Used at the I/O boundary of the Fountain editor: in-memory representation
 * keys paragraph_lines by stable lineId; persisted format keys by lineIndex.
 * `toPersisted(data, lineId => flatLine.lineIndex)` and
 * `fromPersisted(data, lineIndex => flatLine.lineId)` are inverses (for the
 * lines that still exist).
 */
export const translateParagraphLineIndices = (
    data: ContentJsonData,
    lookup: (currentIndex: number) => number | undefined,
): ContentJsonData => {
    if (!data.paragraph_lines?.length) return data;
    const translated: ParagraphLineEntry[] = [];
    for (const entry of data.paragraph_lines) {
        const newIndex = lookup(entry.index);
        if (newIndex === undefined) continue;
        translated.push({ ...entry, index: newIndex });
    }
    return { ...data, paragraph_lines: translated.length > 0 ? translated : undefined };
};

/**
 * Slice 7: enrich in-memory paragraph_lines with composite block context.
 *
 * After hydrate (`translateParagraphLineIndices` returns lineId-keyed entries),
 * this helper looks up each entry's `blockId` + `subField` from the current
 * flat-line snapshot and stores them on the entry. The composite context is a
 * hint — readers may use it to group annotations by block (e.g. "all line tags
 * on this Dialog speaker turn") without re-querying the engine snapshot. It is
 * recomputed every load so a stale block context never leaks back to the wire.
 *
 * `getBlockContext(lineId) → { blockId, subField } | undefined`:
 *   - returns context when the lineId resolves to a flat line;
 *   - returns undefined when no context is available — the entry is KEPT
 *     unchanged (block context is a best-effort hint, never a liveness gate).
 *
 * Enrichment never drops entries: stale-line cleanup is owned solely by
 * `translateParagraphLineIndices` (the I/O boundary). If a lineId momentarily
 * lacks context — e.g. the flatLine maps haven't fully settled — the entry is
 * preserved so a transient lookup miss can't silently delete a user's tag.
 *
 * Migrations from the pre-composite shape (entries without blockId / subField
 * on the wire) are handled here implicitly: the missing fields are filled in
 * on first load, and the next save re-emits them.
 */
export const enrichEntriesWithBlockContext = (
    data: ContentJsonData,
    getBlockContext: (lineId: number) => { blockId: number; subField: SubField } | undefined,
): ContentJsonData => {
    if (!data.paragraph_lines?.length) return data;
    const enriched: ParagraphLineEntry[] = data.paragraph_lines.map(entry => {
        const context = getBlockContext(entry.index);
        if (context === undefined) return entry; // no context — keep entry as-is
        return { ...entry, blockId: context.blockId, subField: context.subField };
    });
    return { ...data, paragraph_lines: enriched };
};
